import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ChevronDown, Sparkles, MoreHorizontal } from "lucide-react";
import api from "@/lib/api";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

const ACTION_ROUTES = {
    open_onboarding:    "/onboarding",
    open_connectors:    "/data",
    open_invite_dialog: "/users",
    open_sense_ai:      "/sense-ai",
};

/**
 * iter-121 Sprint 1 — "◐ 60% complete" profile completion pill.
 *
 * Renders next to the search bar in the TopBar. Click → expands a
 * checklist popover with per-item actions + a "Hide for 7 days" snooze.
 * Hidden when percent === 100 OR snoozed (dismissed_until in future).
 */
export default function ProfileCompletionChip() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/profile/completion");
            setData(r.data);
        } catch {
            setData(null);
        }
    }, []);

    useEffect(() => {
        refresh();
        // Refresh when window regains focus (after navigating to /data etc.).
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [refresh]);

    if (!data || !data.visible) return null;

    const handleAction = (action) => {
        setOpen(false);
        if (!action) return;
        const route = ACTION_ROUTES[action.type];
        if (route) {
            navigate(route);
        } else if (action.type === "resend_verification") {
            api.post("/profile/completion/resend-verification")
                .then(() => toast.success("Verification email sent"))
                .catch((err) => toast.error(err?.response?.data?.detail || "Could not send"));
        }
    };

    const snooze = async () => {
        try {
            await api.post("/profile/completion/dismiss", { days: 7 });
            toast.success("Hidden for 7 days", { description: "We'll remind you next week." });
            setOpen(false);
            await refresh();
        } catch {
            toast.error("Could not snooze");
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="h-9 flex items-center gap-1.5 px-2.5 border border-border bg-secondary/40 hover:bg-secondary rounded-sm transition-colors group"
                    data-testid="profile-completion-chip"
                    aria-label="Profile completion progress"
                    title={`Your profile is ${data.percent}% complete`}
                >
                    <div className="relative w-3.5 h-3.5">
                        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 -rotate-90">
                            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" fill="none" />
                            <circle
                                cx="8" cy="8" r="6.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                className="text-primary"
                                strokeDasharray={`${(data.percent / 100) * 40.84} 40.84`}
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <span className="text-[11px] font-mono uppercase tracking-wider text-foreground" data-testid="profile-completion-percent">
                        {data.percent}% complete
                    </span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-0" data-testid="profile-completion-panel">
                <div className="px-4 py-3 border-b border-border">
                    <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-medium">Get started</span>
                        <span className="ml-auto text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            {data.percent}% complete
                        </span>
                    </div>
                    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${data.percent}%` }}
                            data-testid="profile-completion-bar"
                        />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                        Complete these steps to unlock the full power of your workspace.
                    </p>
                </div>

                <div className="divide-y divide-border max-h-[360px] overflow-y-auto">
                    {data.items.map((item) => (
                        <div
                            key={item.id}
                            className="px-4 py-3 flex items-start gap-3 hover:bg-secondary/30 transition-colors"
                            data-testid={`profile-item-${item.id}`}
                        >
                            <div className={`w-5 h-5 rounded-full mt-0.5 flex items-center justify-center shrink-0 ${
                                item.done ? "bg-emerald-500 text-white" : "bg-secondary border border-border"
                            }`}>
                                {item.done && <Check className="h-3 w-3" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm leading-snug ${item.done ? "text-muted-foreground line-through" : ""}`}>
                                    {item.label}
                                </div>
                                {!item.done && item.action && (
                                    <button
                                        type="button"
                                        onClick={() => handleAction(item.action)}
                                        className="text-[11px] text-primary hover:underline mt-0.5"
                                        data-testid={`profile-action-${item.id}`}
                                    >
                                        {item.action.label} →
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="px-3 py-2 border-t border-border flex items-center justify-between bg-secondary/30">
                    <button
                        type="button"
                        onClick={snooze}
                        className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                        data-testid="profile-completion-snooze"
                    >
                        <MoreHorizontal className="h-3 w-3" /> Hide for 7 days
                    </button>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); navigate("/onboarding"); }}
                        className="text-[11px] text-primary hover:underline"
                        data-testid="profile-completion-resume"
                    >
                        Resume onboarding →
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
